/**
 * Brewski Buckle storefront behavior (ES module, no dependencies).
 * - Product: swipe gallery, style picker, bundle selector with per-unit styles, AJAX add to cart.
 * - Cart: Shipping Protection toggle + guard (never a cart with only protection), checkout rules.
 * - UI: scroll reveal, sticky add to cart, style cards, scroll-to links, in-view video autoplay.
 * Cart changes are announced with Horizon's CartLinesUpdateEvent so the drawer and /cart re-render.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const BB = window.BB || {};
const root = document.documentElement;
root.classList.add('bb-js');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const designMode = Boolean(window.Shopify && window.Shopify.designMode);
const scrollBehavior = reduceMotion ? 'auto' : 'smooth';

/* ---------------- helpers ---------------- */

function formatMoney(cents) {
  const format = BB.moneyFormat || '${{amount}}';
  const value = Number(cents || 0) / 100;
  const delimit = (n, decimals, thousands = ',', decimal = '.') => {
    const [int, frac] = n.toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (decimals ? decimal + frac : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return delimit(value, 0);
      case 'amount_with_comma_separator':
        return delimit(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return delimit(value, 0, '.', ',');
      default:
        return delimit(value, 2);
    }
  });
}

const isSp = (item) => Boolean(BB.spProductId) && item.product_id === BB.spProductId;
const buckleQty = (cart) => (cart?.items || []).filter((i) => !isSp(i)).reduce((n, i) => n + i.quantity, 0);
const spLine = (cart) => (cart?.items || []).find(isSp);
const optedOut = (cart) => cart?.attributes?._bb_sp_optout === '1';

const cartState = { cart: null };
let queue = Promise.resolve();
let pending = 0;

/** Serializes cart work so the guard, toggle and bundle adds never race. */
function enqueue(task) {
  pending += 1;
  const run = queue.then(task, task);
  queue = run.catch(() => {}).finally(() => {
    pending -= 1;
  });
  return run;
}

async function fetchCart() {
  const response = await fetch(BB.routes.cartJs, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  cartState.cart = await response.json();
  return cartState.cart;
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

async function postJSON(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || (data.status && Number(data.status) >= 400)) {
    const error = new Error(data.description || data.message || 'Something went wrong. Please try again.');
    error.data = data;
    throw error;
  }
  return data;
}

/**
 * Runs a cart request and announces it through Horizon's cart event so every cart view morphs.
 * @param {{target?: EventTarget, action: string, context?: string, lines: object[], source: string, run: () => Promise<any>}} opts
 */
async function cartMutation({ target = document, action, context = 'cart', lines, source, run }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(new CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise }));
  try {
    const response = await run();
    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, sections: response?.sections || undefined, source, didError: false },
    });
    syncCartUI(cart);
    return cart;
  } catch (error) {
    try {
      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source, didError: true },
      });
      syncCartUI(cart);
    } catch {
      deferred.reject(error);
    }
    throw error;
  }
}

/** Keeps toggle state and the cart bubble honest (protection is not counted as an item). */
function syncCartUI(cart) {
  if (!cart) return;
  const hasSp = Boolean(spLine(cart));
  const buckles = buckleQty(cart);
  document.querySelectorAll('[data-bb-sp-toggle]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    input.checked = hasSp;
    input.disabled = buckles === 0;
    input.closest('[data-bb-sp]')?.classList.remove('is-busy');
  });
  setTimeout(() => {
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(buckles, false);
    });
  }, 30);
}

function closeCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
  if (drawer && typeof drawer.close === 'function' && drawer.isOpen !== false) drawer.close();
  return drawer;
}

function leaveToProduct() {
  if (designMode) return;
  const drawer = closeCartDrawer();
  const target = new URL(BB.productUrl || '/', window.location.origin);
  if (window.location.pathname !== target.pathname) {
    window.setTimeout(() => window.location.assign(target.href), drawer ? 220 : 0);
  }
}

/* ---------------- cart guard + shipping protection ---------------- */

const spBody = (extra) => ({ sections: cartSectionIds(), sections_url: window.location.pathname, ...extra });

/** Time of the last Shipping Protection toggle; the guard never redirects right after one. */
let lastToggleAt = 0;

async function guard({ reason }) {
  if (!BB.spProductId) return;
  // Buckles in the last cart we saw, before this check (null on first load).
  const previousBuckles = cartState.cart ? buckleQty(cartState.cart) : null;
  const cart = await fetchCart();
  const sp = spLine(cart);
  const buckles = buckleQty(cart);

  if (buckles === 0 && sp) {
    await cartMutation({
      action: 'remove',
      lines: [{ id: sp.key, quantity: 0 }],
      source: 'bb-guard',
      run: () => postJSON(BB.routes.cartChange, spBody({ id: sp.key, quantity: 0 })),
    });
    // Leave for the product page only when the shopper just removed their last buckle,
    // or lands on /cart holding nothing but protection. Never because of the toggle.
    const toggledRecently = Date.now() - lastToggleAt < 5000;
    const removedLastBuckle = reason === 'event' && previousBuckles !== null && previousBuckles > 0;
    const protectionOnlyOnCartPage = reason === 'load' && BB.pageType === 'cart';
    if (!toggledRecently && (removedLastBuckle || protectionOnlyOnCartPage)) leaveToProduct();
    return;
  }

  if (buckles > 0 && !sp && BB.spEnabled && BB.spDefaultOn && BB.spVariantId && !optedOut(cart)) {
    await cartMutation({
      action: 'update',
      lines: [{ merchandiseId: String(BB.spVariantId), quantity: 1 }],
      source: 'bb-guard',
      run: () => postJSON(BB.routes.cartAdd, spBody({ items: [{ id: BB.spVariantId, quantity: 1 }] })),
    });
    return;
  }

  if (sp && (sp.quantity > 1 || !BB.spEnabled)) {
    const quantity = BB.spEnabled ? 1 : 0;
    await cartMutation({
      action: quantity ? 'update' : 'remove',
      lines: [{ id: sp.key, quantity }],
      source: 'bb-guard',
      run: () => postJSON(BB.routes.cartChange, spBody({ id: sp.key, quantity })),
    });
    return;
  }

  syncCartUI(cart);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ detail }) => {
      if (detail?.source === 'bb-guard' || detail?.source === 'bb-sp') return;
      enqueue(() => guard({ reason: 'event' }));
    })
    .catch(() => {});
});

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-bb-sp-toggle]')) return;
  lastToggleAt = Date.now();
  const turnOn = input.checked;
  input.closest('[data-bb-sp]')?.classList.add('is-busy');

  enqueue(async () => {
    const cart = await fetchCart();
    if (turnOn && buckleQty(cart) === 0) {
      syncCartUI(cart);
      return;
    }
    await cartMutation({
      target: input,
      action: turnOn ? 'update' : 'remove',
      lines: [{ merchandiseId: String(BB.spVariantId), quantity: turnOn ? 1 : 0 }],
      source: 'bb-sp',
      run: () =>
        postJSON(
          BB.routes.cartUpdate,
          spBody({ updates: { [BB.spVariantId]: turnOn ? 1 : 0 }, attributes: { _bb_sp_optout: turnOn ? '' : '1' } })
        ),
    });
  }).catch(() => syncCartUI(cartState.cart));
});

/* Checkout: wait for pending cart work and require at least one buckle (drawer, /cart, any link). */
async function verifyCheckout(event, trigger) {
  if (trigger.dataset.bbVerified === 'true') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (trigger instanceof HTMLButtonElement) trigger.setAttribute('aria-busy', 'true');
  await new Promise((resolve) => setTimeout(resolve, 350));
  await queue;
  const cart = await fetchCart().catch(() => null);
  trigger.removeAttribute('aria-busy');
  if (!cart || buckleQty(cart) === 0) {
    syncCartUI(cart);
    if (cart && spLine(cart)) enqueue(() => guard({ reason: 'event' }));
    return;
  }
  window.location.assign('/checkout');
}

document.addEventListener(
  'click',
  (event) => {
    const trigger = event.target instanceof Element && event.target.closest('button[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]');
    if (trigger instanceof HTMLElement) verifyCheckout(event, trigger);
  },
  true
);
document.addEventListener(
  'submit',
  (event) => {
    const submitter = /** @type {SubmitEvent} */ (event).submitter;
    if (submitter instanceof HTMLElement && submitter.getAttribute('name') === 'checkout') verifyCheckout(event, submitter);
  },
  true
);

/* ---------------- product ---------------- */

class BBProduct {
  /** @param {HTMLElement} el */
  constructor(el) {
    this.el = el;
    const json = el.querySelector('[data-bb-product-json]');
    this.data = json ? JSON.parse(json.textContent || '{}') : { variants: [] };
    this.variants = this.data.variants || [];
    this.form = el.querySelector('[data-bb-form]');
    this.bundles = el.querySelector('[data-bb-bundles]');
    this.atc = el.querySelector('[data-bb-atc]');
    // Main style = the style picker if present, else row #1 of the selected bundle.
    this.style =
      el.querySelector('[data-bb-style-input]:checked')?.value ||
      el.querySelector('[data-bb-tier].is-selected [data-bb-unit="0"] [data-bb-unit-input]:checked')?.value ||
      this.variants[0]?.option;
    this.busy = false;
    this.initGallery();
    this.bind();
    this.update();
  }

  variantFor(style) {
    return this.variants.find((v) => v.option === style) || this.variants[0];
  }

  /* gallery */
  initGallery() {
    const track = this.el.querySelector('[data-bb-track]');
    if (!(track instanceof HTMLElement)) return;
    this.track = track;
    this.slides = [...track.querySelectorAll('.bb-gallery__slide')];
    this.thumbs = [...this.el.querySelectorAll('[data-bb-thumb]')];
    this.thumbBar = this.el.querySelector('[data-bb-thumbs]');
    this.counter = this.el.querySelector('[data-bb-counter]');
    this.index = 0;
    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          if (i !== this.index) this.setActive(i);
        });
      },
      { passive: true }
    );
    this.el.querySelectorAll('[data-bb-step]').forEach((btn) =>
      btn.addEventListener('click', () => this.goTo(this.index + Number(btn.getAttribute('data-bb-step'))))
    );
    this.thumbs.forEach((btn) => btn.addEventListener('click', () => this.goTo(Number(btn.getAttribute('data-bb-thumb')))));
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
  }

  goTo(i) {
    if (!this.track || !this.slides.length) return;
    const count = this.slides.length;
    const index = ((i % count) + count) % count;
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: scrollBehavior });
    this.setActive(index);
  }

  goToMedia(mediaId) {
    if (!mediaId || !this.slides) return;
    const index = this.slides.findIndex((s) => s.getAttribute('data-media-id') === String(mediaId));
    if (index >= 0) this.goTo(index);
  }

  setActive(index) {
    this.index = index;
    if (this.counter) this.counter.textContent = String(index + 1);
    this.thumbs.forEach((t, i) => {
      if (i === index) {
        t.setAttribute('aria-current', 'true');
        if (this.thumbBar instanceof HTMLElement) {
          const left = t.offsetLeft - (this.thumbBar.clientWidth - t.clientWidth) / 2;
          this.thumbBar.scrollTo({ left, behavior: scrollBehavior });
        }
      } else t.removeAttribute('aria-current');
    });
  }

  /* selection */
  bind() {
    this.el.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.matches('[data-bb-style-input]')) this.selectStyle(input.value);
      else if (input.matches('[data-bb-tier-input]')) this.selectTier(input.closest('[data-bb-tier]'));
      else if (input.matches('[data-bb-unit-input]')) {
        const unit = input.closest('[data-bb-unit]');
        if (unit instanceof HTMLElement) unit.dataset.touched = 'true';
        if (unit?.getAttribute('data-bb-unit') === '0') this.selectStyle(input.value);
        else {
          this.goToMedia(this.variantFor(input.value)?.mediaId);
          this.update();
        }
      }
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.addToCart();
    });
    this.el.querySelector('[data-bb-fast]')?.addEventListener('click', () => this.addToCart({ checkout: true }));
    document.addEventListener('bb:add-request', () => this.addToCart());
    document.addEventListener('bb:select-style', (event) => {
      const value = /** @type {CustomEvent} */ (event).detail?.style;
      if (value) this.selectStyle(value);
    });
  }

  selectStyle(value) {
    if (!value) return;
    this.style = value;
    this.el.querySelectorAll('[data-bb-style-input]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = input.value === value;
    });
    const name = this.el.querySelector('[data-bb-style-name]');
    if (name) name.textContent = value;
    // Row #1 of every bundle mirrors the main style; untouched rows #2/#3 follow it too.
    this.el.querySelectorAll('[data-bb-unit]').forEach((unit) => {
      if (!(unit instanceof HTMLElement)) return;
      const isFirst = unit.getAttribute('data-bb-unit') === '0';
      if (!isFirst && unit.dataset.touched === 'true') return;
      unit.querySelectorAll('[data-bb-unit-input]').forEach((input) => {
        if (input instanceof HTMLInputElement) input.checked = input.value === value;
      });
    });
    this.goToMedia(this.variantFor(value)?.mediaId);
    this.update();
  }

  selectTier(tier) {
    if (!(tier instanceof HTMLElement)) return;
    this.el.querySelectorAll('[data-bb-tier]').forEach((t) => t.classList.toggle('is-selected', t === tier));
    this.update();
  }

  currentTier() {
    return this.el.querySelector('[data-bb-tier].is-selected') || this.el.querySelector('[data-bb-tier]');
  }

  /** Styles for each unit of the selected bundle (unit 1 = main style). */
  unitStyles() {
    const tier = this.currentTier();
    const qty = Number(tier?.getAttribute('data-qty') || 1);
    const units = [...(tier?.querySelectorAll('[data-bb-unit]') || [])];
    const styles = [];
    for (let i = 0; i < qty; i += 1) {
      const checked = units[i]?.querySelector('[data-bb-unit-input]:checked');
      styles.push(i === 0 ? this.style : checked instanceof HTMLInputElement ? checked.value : this.style);
    }
    return styles;
  }

  update() {
    const variant = this.variantFor(this.style);
    if (!variant) return;
    const tier = this.currentTier();
    const pct = Number(tier?.getAttribute('data-pct') || 0);
    const styles = this.unitStyles();
    const unitVariants = styles.map((s) => this.variantFor(s));
    const total = unitVariants.reduce((sum, v) => sum + Math.round((v.price * (100 - pct)) / 100), 0);
    const regular = unitVariants.reduce((sum, v) => sum + v.price, 0);
    const compare = unitVariants.reduce((sum, v) => sum + Math.max(v.compare, v.price), 0);
    const basis = this.bundles?.getAttribute('data-savings-basis') === 'regular' ? regular : compare;
    const save = Math.max(basis - total, 0);

    // price block follows the main style
    const now = this.el.querySelector('[data-bb-price-now]');
    if (now) now.textContent = formatMoney(variant.price);

    // tier cards follow the main style price
    this.el.querySelectorAll('[data-bb-tier]').forEach((t) => {
      const q = Number(t.getAttribute('data-qty') || 1);
      const p = Number(t.getAttribute('data-pct') || 0);
      const unit = Math.round((variant.price * (100 - p)) / 100);
      const tt = t.querySelector('[data-bb-tier-total]');
      const tc = t.querySelector('[data-bb-tier-compare]');
      const te = t.querySelector('[data-bb-tier-each]');
      if (tt) tt.textContent = formatMoney(t === tier ? total : unit * q);
      if (tc) tc.lastChild.textContent = formatMoney(Math.max(variant.compare, variant.price) * q);
      if (te) te.textContent = `${formatMoney(unit)} ${this.bundles?.getAttribute('data-each-text') || ''}`.trim();
    });

    const totalEl = this.el.querySelector('[data-bb-atc-total]');
    if (totalEl) totalEl.textContent = formatMoney(total);
    const saveLine = this.el.querySelector('[data-bb-save]');
    const saveAmount = this.el.querySelector('[data-bb-save-amount]');
    if (saveAmount) saveAmount.textContent = formatMoney(save);
    if (saveLine instanceof HTMLElement) saveLine.hidden = save <= 0;

    const variantInput = this.el.querySelector('[data-bb-variant-input]');
    const qtyInput = this.el.querySelector('[data-bb-qty-input]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = String(unitVariants[0].id);
    if (qtyInput instanceof HTMLInputElement) qtyInput.value = String(styles.length);

    const mixed = new Set(styles).size > 1;
    this.form?.classList.toggle('is-mixed', mixed);
    const fast = this.el.querySelector('[data-bb-fast]');
    if (fast instanceof HTMLElement) fast.hidden = !mixed;

    const tierTitle = tier?.querySelector('.bb-tier__title')?.firstChild?.textContent?.trim() || '';
    const label = mixed ? styles.join(' + ') : `${this.style}${styles.length > 1 ? ` ×${styles.length}` : ''}`;
    document.dispatchEvent(
      new CustomEvent('bb:state', {
        detail: { line: tierTitle ? `${tierTitle} · ${label}` : label, total: formatMoney(total), image: variant.image },
      })
    );
  }

  async addToCart({ checkout = false } = {}) {
    if (this.busy || !this.atc) return;
    this.busy = true;
    const error = this.el.querySelector('[data-bb-error]');
    if (error instanceof HTMLElement) error.hidden = true;
    this.atc.classList.add('is-loading');
    this.atc.setAttribute('aria-busy', 'true');

    const counts = new Map();
    this.unitStyles().forEach((style) => {
      const id = this.variantFor(style).id;
      counts.set(id, (counts.get(id) || 0) + 1);
    });
    const items = [...counts].map(([id, quantity]) => ({ id, quantity }));

    try {
      await enqueue(async () => {
        const cart = cartState.cart || (await fetchCart());
        if (BB.spEnabled && BB.spDefaultOn && BB.spVariantId && !spLine(cart) && !optedOut(cart)) {
          items.push({ id: BB.spVariantId, quantity: 1 });
        }
        await cartMutation({
          target: this.atc,
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          source: 'bb-bundles',
          run: () => postJSON(BB.routes.cartAdd, { items, sections: cartSectionIds(), sections_url: window.location.pathname }),
        });
      });
      if (checkout) window.location.assign('/checkout');
    } catch (err) {
      if (error instanceof HTMLElement) {
        error.textContent = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
        error.hidden = false;
      }
    } finally {
      this.busy = false;
      this.atc.classList.remove('is-loading');
      this.atc.removeAttribute('aria-busy');
    }
  }
}

/* ---------------- page UI ---------------- */

let revealObserver = null;
function initReveal(scope = document) {
  const items = scope.querySelectorAll('[data-bb-reveal]:not(.is-visible)');
  if (designMode || reduceMotion || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-visible'));
    return;
  }
  revealObserver ||= new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );
  items.forEach((el) => revealObserver.observe(el));
}

function initVideos(scope = document) {
  const videos = [...scope.querySelectorAll('[data-bb-autoplay] video')];
  if (!videos.length) return;
  videos.forEach((video) => {
    video.muted = true;
    video.playsInline = true;
    video.loop = true;
    video.removeAttribute('controls');
  });
  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = /** @type {HTMLVideoElement} */ (entry.target);
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      });
    },
    { threshold: 0.25 }
  );
  videos.forEach((v) => observer.observe(v));
}

/* Express checkout: mount Shopify's payment button only when the buy box gets close. */
function initDynamicCheckout(scope = document) {
  scope.querySelectorAll('[data-bb-dynamic]').forEach((holder) => {
    const template = holder.querySelector('template[data-bb-dynamic-template]');
    if (!(template instanceof HTMLTemplateElement)) return;
    let mounted = false;
    const mount = () => {
      if (mounted) return;
      mounted = true;
      holder.append(template.content.cloneNode(true));
      template.remove();
    };
    if (designMode || !('IntersectionObserver' in window)) return mount();
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          mount();
        }
      },
      { rootMargin: '300px 0px' }
    );
    observer.observe(holder);
    ['pointerdown', 'keydown'].forEach((type) => window.addEventListener(type, mount, { once: true, passive: true }));
  });
}

/* Header ticker: constant speed in px/s whatever the message length, pause while touched. */
function initTickers(scope = document) {
  scope.querySelectorAll('[data-bb-ticker]').forEach((ticker) => {
    if (!(ticker instanceof HTMLElement) || ticker.dataset.bbReady) return;
    ticker.dataset.bbReady = 'true';
    const track = ticker.querySelector('[data-bb-ticker-track]');
    if (!(track instanceof HTMLElement)) return;
    const pxPerSecond = Number(ticker.dataset.speed) || 56;
    const setDuration = () => {
      const half = track.scrollWidth / 2;
      if (half > 0) ticker.style.setProperty('--bb-tk-duration', `${(half / pxPerSecond).toFixed(2)}s`);
    };
    setDuration();
    document.fonts?.ready.then(setDuration).catch(() => {});

    if (!ticker.classList.contains('bb-ticker--pause')) return;
    const pause = () => ticker.classList.add('is-paused');
    const resume = () => ticker.classList.remove('is-paused');
    ticker.addEventListener('touchstart', pause, { passive: true });
    ticker.addEventListener('touchend', resume, { passive: true });
    ticker.addEventListener('touchcancel', resume, { passive: true });
  });
}

/* Hide the sticky bar while any drawer/dialog is open (cheaper than body:has()). */
document.addEventListener('theme-drawer:open', () => root.classList.add('bb-drawer-open'));
document.addEventListener('theme-drawer:close', () => {
  if (!document.querySelector('theme-drawer[open]')) root.classList.remove('bb-drawer-open');
});

function initSticky() {
  const sticky = document.querySelector('[data-bb-sticky]');
  const atc = document.querySelector('[data-bb-atc]');
  if (!(sticky instanceof HTMLElement) || !(atc instanceof HTMLElement)) return;
  const button = sticky.querySelector('[data-bb-sticky-btn]');
  const line = sticky.querySelector('[data-bb-sticky-line]');
  const price = sticky.querySelector('[data-bb-sticky-price]');
  const img = sticky.querySelector('img');
  document.body.classList.add('bb-has-sticky');

  new IntersectionObserver(([entry]) => {
    const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    sticky.classList.toggle('is-visible', show);
    sticky.setAttribute('aria-hidden', String(!show));
    if (button instanceof HTMLElement) button.tabIndex = show ? 0 : -1;
  }).observe(atc);

  document.addEventListener('bb:state', (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    if (line && detail.line) line.textContent = detail.line;
    if (price && detail.total) price.textContent = detail.total;
    if (img instanceof HTMLImageElement && detail.image) {
      img.removeAttribute('srcset');
      img.src = detail.image;
    }
  });
  button?.addEventListener('click', () => document.dispatchEvent(new CustomEvent('bb:add-request')));
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const styleLink = event.target.closest('[data-bb-select-style]');
  if (styleLink && document.querySelector('[data-bb-product]')) {
    event.preventDefault();
    document.dispatchEvent(new CustomEvent('bb:select-style', { detail: { style: styleLink.getAttribute('data-bb-select-style') } }));
    document.querySelector('#bb-bundles')?.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
    return;
  }

  const scrollLink = event.target.closest('[data-bb-scroll-to]');
  if (scrollLink) {
    const target = document.querySelector(scrollLink.getAttribute('data-bb-scroll-to') || '');
    if (target) {
      event.preventDefault();
      target.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
    }
  }
});

function init(scope = document) {
  scope.querySelectorAll('[data-bb-product]').forEach((el) => {
    if (el instanceof HTMLElement && !el.dataset.bbReady) {
      el.dataset.bbReady = 'true';
      new BBProduct(el);
    }
  });
  initReveal(scope);
  initVideos(scope);
  initDynamicCheckout(scope);
  initTickers(scope);
}

// The sticky bar listens before the product publishes its first state, so it shows the
// preselected bundle (title, styles, price) from the first paint of the bar.
initSticky();
init();
enqueue(() => guard({ reason: 'load' })).catch(() => {});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
